import { useEffect, useRef, useState } from 'react'
import { useAppStore } from '../../store/app'
import {
  deleteAccount,
  deleteAccountPreflight,
  requestAuthCode,
  requestStepUpToken,
  type AccountDeletionPreview,
  type AccountDeletionResult,
} from '../../api/client'
import { logout } from '../../api/logout'
import { startWS, stopWS } from '../../api/ws'
import { Group, SectionHeader } from './AccountComponents'
import './AccountStyles.css'
import '../household/DeleteHouseholdSheet.css'

// Deleting your account, one step per screen, like deleting a household:
// what goes (and who loses access), your computers, confirm with an emailed
// code, done. Households you own are deleted; the ones you're a member of you
// just leave, and any computer of yours serving one stops working for it. The
// last screen lists what's left to do by hand (also emailed, since you're
// signed out after). See relay account_delete_cleanup.go.

type Step = 'what' | 'computers' | 'confirm' | 'deleting' | 'done'

export function DeleteAccountSheet() {
  const email = useAppStore((s) => s.email) ?? ''

  const [preview, setPreview] = useState<AccountDeletionPreview | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [step, setStep] = useState<Step>('what')
  const [acknowledged, setAcknowledged] = useState<Record<string, boolean>>({})
  const [codeSent, setCodeSent] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<AccountDeletionResult | null>(null)
  const codeRef = useRef<HTMLInputElement>(null)
  const deleted = useRef(false)

  useEffect(() => {
    deleteAccountPreflight()
      .then(setPreview)
      .catch((e) => setLoadError(e instanceof Error ? e.message : String(e)))
  }, [])

  useEffect(() => {
    if (codeSent) setTimeout(() => codeRef.current?.focus(), 50)
  }, [codeSent])

  // Once the account is gone this device's credentials are too: however the
  // sheet closes (Finish, or the sheet's own Done), finish signing out.
  useEffect(
    () => () => {
      if (deleted.current) void logout()
    },
    [],
  )

  if (loadError) {
    return (
      <div className="about">
        <p className="about__error">{loadError}</p>
      </div>
    )
  }
  if (!preview) {
    return (
      <div className="about">
        <p className="about__hint">Loading…</p>
      </div>
    )
  }

  // A relay predating the wizard sends only affected_orgs.
  const householdsDeleted =
    preview.households_deleted ??
    preview.affected_orgs.map((o) => ({ id: o.id, name: o.name, other_members: [] as string[] }))
  const householdsLeft = preview.households_left ?? []
  const hosts = preview.hosts ?? []
  const manualHosts = hosts.filter((h) => !h.auto_cleanup)
  const steps: Step[] = ['what', ...(hosts.length > 0 ? (['computers'] as Step[]) : []), 'confirm']

  function next() {
    const i = steps.indexOf(step)
    setError(null)
    if (i >= 0 && i < steps.length - 1) setStep(steps[i + 1])
  }
  function back() {
    const i = steps.indexOf(step)
    setError(null)
    if (i > 0) setStep(steps[i - 1])
  }

  async function sendCode() {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await requestAuthCode(email)
      setCodeSent(true)
    } catch {
      setError('Could not send the code. Check your connection and try again.')
    } finally {
      setBusy(false)
    }
  }

  async function confirmDelete() {
    if (code.length !== 6 || busy) return
    setBusy(true)
    setError(null)
    let stepupToken: string
    try {
      stepupToken = await requestStepUpToken(email, code)
    } catch {
      setBusy(false)
      setCode('')
      setError('That code didn’t work. Check it, or send a new one.')
      return
    }
    setStep('deleting')
    // The live connection would notice this device vanish mid-delete and sign
    // out on its own, closing this sheet before its last screen. Hold it off.
    stopWS()
    try {
      const res = await deleteAccount(stepupToken)
      deleted.current = true
      setResult(res)
      setStep('done')
    } catch (e) {
      startWS()
      setStep('confirm')
      setCodeSent(false)
      setCode('')
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  function finish() {
    // Signing out unmounts this sheet; don't sign out twice.
    deleted.current = false
    void logout()
  }

  const stepNumber = steps.indexOf(step) + 1
  const progress =
    stepNumber > 0 ? <p className="delete-household__progress">Step {stepNumber} of {steps.length}</p> : null

  // ---- 1. What will be deleted ----
  if (step === 'what') {
    return (
      <div className="about">
        {progress}
        <SectionHeader>Your account will be deleted</SectionHeader>
        <Group>
          <div className="delete-household__body">
            <p>
              Your account, your devices and everything you’ve set up will be deleted. This is permanent. There’s no
              undo.
            </p>
          </div>
        </Group>

        {householdsDeleted.length > 0 && (
          <>
            <SectionHeader>Households deleted with it</SectionHeader>
            <Group>
              <div className="delete-household__body">
                {householdsDeleted.map((h) => (
                  <div key={h.id}>
                    <div className="delete-household__row-name">{h.name}</div>
                    <div className="delete-household__note">
                      {h.other_members.length > 0
                        ? `${h.other_members.join(', ')} ${h.other_members.length === 1 ? 'loses' : 'lose'} access. We’ll email them.`
                        : 'Everything in it goes: agents, chats, files and routines.'}
                    </div>
                  </div>
                ))}
              </div>
            </Group>
          </>
        )}

        {householdsLeft.length > 0 && (
          <>
            <SectionHeader>Households you’ll leave</SectionHeader>
            <Group>
              <div className="delete-household__body">
                {householdsLeft.map((h) => (
                  <div key={h.id} className="delete-household__row-name">
                    {h.name}
                  </div>
                ))}
                <p className="delete-household__note">They carry on without you.</p>
              </div>
            </Group>
          </>
        )}

        <StepButtons onNext={next} />
      </div>
    )
  }

  // ---- 2. Computers ----
  if (step === 'computers') {
    const allAcknowledged = manualHosts.every((h) => acknowledged[h.host_id])
    return (
      <div className="about">
        {progress}
        <SectionHeader>Computers running Hearth</SectionHeader>
        <Group>
          <div className="delete-household__body">
            {hosts.map((h) => (
              <div key={h.host_id} className="delete-household__host">
                <div className="delete-household__row">
                  <span className="delete-household__row-name">{h.hostname}</span>
                  <span className="delete-household__row-meta">
                    {h.household} · {h.online ? 'Online' : 'Offline'}
                  </span>
                </div>
                <p className="delete-household__note">
                  {h.household_deleted
                    ? `${h.yours ? 'It' : 'This computer isn’t yours, but it'} runs Hearth for ${h.household}, which is being deleted.`
                    : `It stops working for ${h.household}, which carries on without it.`}{' '}
                  {h.auto_cleanup
                    ? `Hearth will stop its agents, delete ${h.household}’s folders on it, and sign it out.`
                    : `${h.online ? 'It runs an older version of Hearth that can’t clean up after itself.' : 'It can’t be reached right now.'} Its files stay until you remove them: on that computer, run `}
                  {!h.auto_cleanup && (
                    <>
                      <code>hearth stop</code>, then delete <code>~/.hearth</code> and the household’s folder in{' '}
                      <code>~/hearth_agents</code>.
                    </>
                  )}
                </p>
                {!h.auto_cleanup && (
                  <label className="delete-household__check">
                    <input
                      type="checkbox"
                      checked={!!acknowledged[h.host_id]}
                      onChange={(e) => setAcknowledged((a) => ({ ...a, [h.host_id]: e.target.checked }))}
                    />
                    <span>I understand files stay on {h.hostname}</span>
                  </label>
                )}
              </div>
            ))}
          </div>
        </Group>
        <StepButtons onBack={back} onNext={next} nextDisabled={!allAcknowledged} />
      </div>
    )
  }

  // ---- 4. Done ----
  if (step === 'done' && result) {
    const manual = result.manual_steps ?? []
    const cleaned = (result.hosts ?? []).filter((h) => h.cleaned_up)
    return (
      <div className="about">
        <SectionHeader>Your account was deleted</SectionHeader>
        {cleaned.length > 0 && (
          <Group>
            <div className="delete-household__body">
              {cleaned.map((h) => (
                <p key={h.host_id} className="delete-household__note">
                  {h.hostname}: agents stopped, {h.household}’s folders deleted, signed out.
                </p>
              ))}
            </div>
          </Group>
        )}
        {manual.length > 0 ? (
          <>
            <SectionHeader>Still to do by hand</SectionHeader>
            <Group>
              <div className="delete-household__body">
                <ol className="delete-household__steps">
                  {manual.map((m, i) => (
                    <li key={i}>
                      <div className="delete-household__row-name">{m.title}</div>
                      <div className="delete-household__note">{m.detail}</div>
                    </li>
                  ))}
                </ol>
                <p className="delete-household__note">We’ve emailed you this list too.</p>
              </div>
            </Group>
          </>
        ) : (
          <p className="about__hint delete-household__hint">There’s nothing left for you to do.</p>
        )}
        <div className="delete-household__buttons">
          <button className="delete-household__button delete-household__button--primary" onClick={finish}>
            Finish
          </button>
        </div>
      </div>
    )
  }

  // ---- 3. Confirm (and deleting) ----
  const deleting = step === 'deleting'
  return (
    <div className="about">
      {progress}
      <SectionHeader>Confirm</SectionHeader>
      <Group>
        <div className="delete-household__body">
          {!codeSent ? (
            <button className="delete-household__button" onClick={() => void sendCode()} disabled={busy}>
              {busy ? 'Sending code…' : `Email a code to ${email}`}
            </button>
          ) : (
            <>
              <label className="delete-household__label" htmlFor="delete-account-code">
                Enter the 6-digit code we sent to {email}
              </label>
              <input
                id="delete-account-code"
                ref={codeRef}
                className="delete-household__input delete-household__input--code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                value={code}
                maxLength={6}
                disabled={deleting}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void confirmDelete()
                }}
              />
              <button
                className="delete-household__button delete-household__button--danger"
                onClick={() => void confirmDelete()}
                disabled={code.length !== 6 || busy || deleting}
              >
                {deleting ? 'Deleting…' : busy ? 'Checking…' : 'Delete my account permanently'}
              </button>
              {!deleting && (
                <button
                  className="delete-household__link"
                  onClick={() => {
                    setCodeSent(false)
                    setCode('')
                    setError(null)
                  }}
                  disabled={busy}
                >
                  Send a new code
                </button>
              )}
            </>
          )}
          {deleting && (
            <p className="delete-household__note">
              {hosts.some((h) => h.auto_cleanup)
                ? 'Stopping agents and cleaning up your computers. This can take up to half a minute.'
                : 'This takes a few seconds.'}
            </p>
          )}
        </div>
      </Group>
      {error && <p className="about__error">{error}</p>}
      {!deleting && <StepButtons onBack={back} />}
    </div>
  )
}

function StepButtons({
  onBack,
  onNext,
  nextDisabled,
}: {
  onBack?: () => void
  onNext?: () => void
  nextDisabled?: boolean
}) {
  return (
    <div className="delete-household__buttons">
      {onBack && (
        <button className="delete-household__button" onClick={onBack}>
          Back
        </button>
      )}
      {onNext && (
        <button
          className="delete-household__button delete-household__button--primary"
          onClick={onNext}
          disabled={nextDisabled}
        >
          Next
        </button>
      )}
    </div>
  )
}
